import { useState } from 'react';
import { Button, Input, Select } from '../ui';
import { api } from '../../lib/api';
import { ARCHETYPE_PRESETS, type Archetype, type CapitalStructure } from '../../utils/archetypePresets';
import type { InvestorProfile } from '../../types';

export interface BriefAxes {
  archetype: Archetype | null;
  capital_structure: CapitalStructure | null;
  down_payment_target: number | null;
  investor_profile_id: string | null;
  is_1031: boolean;
  /**
   * 🚨 BOTH OF THESE WERE PARSED AND THEN DROPPED. The backend has returned `holding_period`
   * since the parser was written, and `exit_cap_rate` was added 2026-08-27 - but `BriefAxes`
   * carried five fields, so `handleAxisChange` never saw either and the clause silently
   * vanished. Joseph typed "10 percent exit cap" and watched nothing happen.
   * ⭐ `handleAxisChange` spreads whatever it is handed onto the analysis, so naming the field
   * here is what applies it.
   */
  holding_period: number | null;
  exit_cap_rate: number | null;
}

interface Props {
  value: BriefAxes;
  profiles: InvestorProfile[];
  onChange: (next: Partial<BriefAxes>, origin: 'brief' | 'user') => void;
}

const STRUCTURE_LABELS: Record<CapitalStructure, string> = {
  equity_first: 'Fixed cash down, solve loan',
  ltv_driven: 'LTV drives loan',
  all_cash: 'All cash',
  assume_existing: 'Assume existing loan',
  seller_carry: 'Seller carryback',
  sba_504: 'SBA 504',
  sba_7a: 'SBA 7(a)',
};

export default function DealBriefStrip({ value, profiles, onChange }: Props) {
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  const [readAs, setReadAs] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submitBrief = async () => {
    if (!brief.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.parseDealBrief(brief);
      onChange(
        {
          archetype: (r.archetype as Archetype) ?? value.archetype,
          capital_structure: (r.capital_structure as CapitalStructure) ?? value.capital_structure,
          down_payment_target: r.down_payment_target ?? value.down_payment_target,
          investor_profile_id: r.investor_profile_id ?? value.investor_profile_id,
          is_1031: r.is_1031,
          holding_period: r.holding_period ?? value.holding_period,
          exit_cap_rate: r.exit_cap_rate ?? value.exit_cap_rate,
        },
        'brief'
      );
      const parts: string[] = [];
      if (r.archetype) parts.push(ARCHETYPE_PRESETS[r.archetype as Archetype].label);
      if (r.down_payment_target) parts.push(`$${r.down_payment_target.toLocaleString()} down`);
      if (r.capital_structure) parts.push(STRUCTURE_LABELS[r.capital_structure as CapitalStructure]);
      const profile = profiles.find(p => p.id === r.investor_profile_id);
      if (profile) parts.push(`${profile.name}'s thresholds`);
      // Echo everything that was applied, so "read as" is the whole truth and not a subset.
      if (r.holding_period) parts.push(`${r.holding_period}-yr hold`);
      if (r.exit_cap_rate) parts.push(`${r.exit_cap_rate}% exit cap`);
      setReadAs(parts.join(' | ') || 'Nothing determined. Set the fields below.');
      if (r.unresolved.length) {
        setError(`Could not determine: ${r.unresolved.join(', ')}. Set them below.`);
      }
    } catch {
      setError('Could not read that brief. Set the fields below.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-gray-50 border rounded-xl p-4 mb-4 space-y-3">
      <div className="flex gap-2">
        <Input
          className="flex-1"
          placeholder="3 million down, rest new loan, NNN retail, technical investor"
          value={brief}
          onChange={e => setBrief(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submitBrief(); }}
        />
        <Button onClick={submitBrief} isLoading={busy} disabled={busy}>Read brief</Button>
      </div>

      {readAs && <p className="text-sm text-gray-700">Read as: {readAs}</p>}
      {error && <p className="text-sm text-warning">{error}</p>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Select takes a REQUIRED `options` array prop and renders the <option>
            elements itself. It does not accept children - passing them is both a
            TS error (options is required) and silently dropped at runtime, giving
            an empty dropdown. See frontend/src/components/ui/Input.tsx SelectProps,
            and the in-repo usage at InvestmentAnalysis.tsx:1467. */}
        <Select
          label="Archetype"
          value={value.archetype ?? ''}
          onChange={e => onChange({ archetype: (e.target.value || null) as Archetype }, 'user')}
          options={[
            { value: '', label: 'Not set' },
            ...Object.values(ARCHETYPE_PRESETS).map(p => ({ value: p.key, label: p.label })),
          ]}
        />

        <Select
          label="Capital structure"
          value={value.capital_structure ?? ''}
          onChange={e => onChange({ capital_structure: (e.target.value || null) as CapitalStructure }, 'user')}
          options={[
            { value: '', label: 'Not set' },
            ...(Object.keys(STRUCTURE_LABELS) as CapitalStructure[]).map(k => ({
              value: k,
              label: STRUCTURE_LABELS[k],
            })),
          ]}
        />

        <Input
          label="Cash to invest"
          type="number"
          leftAddon="$"
          disabled={value.capital_structure !== 'equity_first'}
          value={value.down_payment_target ?? ''}
          onChange={e => onChange({ down_payment_target: Number(e.target.value) || null }, 'user')}
        />

        <Select
          label="Investor"
          value={value.investor_profile_id ?? ''}
          onChange={e => onChange({ investor_profile_id: e.target.value || null }, 'user')}
          options={[
            { value: '', label: 'Not set' },
            ...profiles.map(p => ({ value: p.id, label: p.name })),
          ]}
        />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={value.is_1031}
          onChange={e => onChange({ is_1031: e.target.checked }, 'user')}
        />
        1031 exchange (works with any capital structure)
      </label>
    </div>
  );
}
